import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronLeft, Truck, Package, CheckCircle, 
  Clock, CreditCard, MapPin, User, Weight, DollarSign, 
  FileText, Download, Phone, Eye, Navigation, ChevronDown, ChevronUp, AlertCircle, XCircle, Receipt
} from 'lucide-react';

const DELIVERY_STATUS_CONFIG = {
  '待送货': { color: 'bg-slate-500', icon: Clock, textColor: 'text-slate-600', lightBg: 'bg-slate-100' },
  '已到场': { color: 'bg-blue-500', icon: Truck, textColor: 'text-blue-600', lightBg: 'bg-blue-100' },
  '已入库': { color: 'bg-purple-500', icon: Package, textColor: 'text-purple-600', lightBg: 'bg-purple-100' },
  '待结算': { color: 'bg-orange-500', icon: AlertCircle, textColor: 'text-orange-600', lightBg: 'bg-orange-100' },
  '已结算': { color: 'bg-amber-500', icon: DollarSign, textColor: 'text-amber-600', lightBg: 'bg-amber-100' },
  '已开票': { color: 'bg-teal-500', icon: Receipt, textColor: 'text-teal-600', lightBg: 'bg-teal-100' },
  '已付款': { color: 'bg-green-500', icon: CreditCard, textColor: 'text-green-600', lightBg: 'bg-green-100' },
};

const STATUS_ORDER = ['待送货', '已到场', '已入库', '待结算', '已结算', '已开票', '已付款'];
const IN_PROGRESS_STATUSES = ['已到场', '已入库'];
const NEED_CONFIRM_STATUS = '待结算';

const SettlementConfirmSection = ({ delivery, onConfirm, onReject }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  return (
    <>
      <div className="pt-4 -mx-4 px-4 pb-3 bg-gradient-to-b from-orange-50 to-transparent border-b-2 border-orange-200">
        <div className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
          <AlertCircle size={12} />
          结算确认
        </div>
        
        <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
          <div className="flex items-center justify-between py-2 border-b border-slate-100 mb-3">
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">入库重量</div>
              <div className="text-base font-bold text-slate-800">{delivery.weight}吨</div>
            </div>
            <div className="text-slate-300 text-lg">×</div>
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">结算单价</div>
              <div className="text-base font-bold text-slate-800">¥{delivery.unitPrice}/吨</div>
            </div>
            <div className="text-slate-300 text-lg">=</div>
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">结算金额</div>
              <div className="text-xl font-bold text-red-600">¥{delivery.amount.toLocaleString()}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowRejectModal(true)}
              className="flex-1 py-2.5 rounded-lg border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
            >
              <XCircle size={16} />
              驳回
            </button>
            <button 
              onClick={() => setShowConfirmModal(true)}
              className="flex-1 py-2.5 rounded-lg bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-1 shadow-sm"
            >
              <CheckCircle size={16} />
              确认结算
            </button>
          </div>
        </div>
      </div>
      
      {/* 确认弹窗 */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">确认结算</h3>
              <p className="text-sm text-slate-500 mb-2">
                确认结算金额 <span className="text-red-600 font-bold">¥{delivery.amount.toLocaleString()}</span> ？
              </p>
              <p className="text-xs text-slate-400">确认后将进入付款流程</p>
            </div>
            <div className="flex border-t border-slate-200">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
              >
                取消
              </button>
              <button 
                onClick={() => {
                  onConfirm(delivery.id);
                  setShowConfirmModal(false);
                }}
                className="flex-1 py-3 text-green-600 font-bold border-l border-slate-200 hover:bg-green-50 transition-colors"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 驳回弹窗 */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">驳回结算</h3>
                  <p className="text-xs text-slate-500">请填写驳回原因</p>
                </div>
              </div>
              
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="请输入驳回原因，如：重量有误、价格不对等..."
                className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
              
              <div className="flex gap-3 mt-4">
                <button 
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  取消
                </button>
                <button 
                  onClick={() => {
                    onReject(delivery.id, rejectReason);
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  disabled={!rejectReason.trim()}
                  className={`flex-1 py-2.5 rounded-lg font-bold transition-colors ${
                    rejectReason.trim() 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  提交驳回
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MOCK_ORDER = {
  orderNo: 'ORD20260218001',
  status: '送货中',
  baseName: '马钢诚兴马鞍山基地',
  baseAddress: '安徽省马鞍山市雨山区',
  material: '重型废钢 (HMS1)',
  estimatedWeight: 50,
  estimatedAmount: 132500,
  actualWeight: 27.5,
  actualAmount: 72625,
  progress: { total: 5, waiting: 1, inProgress: 1, needConfirm: 1, completed: 2 },
  deliveries: [
    { 
      id: 'D001', 
      deliveryNo: 'DEL20260218001', 
      status: '已付款', 
      weight: 10.2, 
      amount: 27030, 
      vehicleNo: '皖E12345', 
      driverName: '张师傅', 
      driverPhone: '138****1234',
      timeline: [
        { time: '02-18 10:00', status: '待送货', action: '创建预约单', document: '预约单.pdf' },
        { time: '02-18 11:30', status: '已到场', action: '车辆到场确认', document: '到场确认单.pdf' },
        { time: '02-18 14:00', status: '已入库', action: '入库过磅 10.2吨', document: '过磅单.pdf' },
        { time: '02-18 16:00', status: '已结算', action: '结算确认 ¥27,030', document: '结算单.pdf' },
        { time: '02-18 18:00', status: '已付款', action: '付款完成', document: '付款凭证.pdf' },
      ]
    },
    { 
      id: 'D002', 
      deliveryNo: 'DEL20260218002', 
      status: '待结算',
      weight: 12.3,
      unitPrice: 2650,
      amount: 32595,
      vehicleNo: '皖E23456', 
      driverName: '李师傅', 
      driverPhone: '139****5678',
      timeline: [
        { time: '02-18 14:30', status: '待送货', action: '创建预约单', document: '预约单.pdf' },
        { time: '02-18 15:00', status: '已到场', action: '车辆到场确认', document: '到场确认单.pdf' },
        { time: '02-18 17:00', status: '已入库', action: '入库过磅 12.3吨', document: '过磅单.pdf' },
        { time: '02-18 18:00', status: '待结算', action: '结算单待确认', document: '结算单.pdf' },
      ]
    },
    { 
      id: 'D003', 
      deliveryNo: 'DEL20260218003', 
      status: '已到场', 
      vehicleNo: '皖E34567', 
      driverName: '王师傅', 
      driverPhone: '137****9012',
      timeline: [
        { time: '02-18 16:00', status: '待送货', action: '创建预约单', document: '预约单.pdf' },
        { time: '02-18 17:30', status: '已到场', action: '车辆到场确认', document: '到场确认单.pdf' },
      ]
    },
  ]
};

const TimelineNode = ({ item, isLast, isCompleted, isCurrent }) => {
  const config = DELIVERY_STATUS_CONFIG[item.status];
  const Icon = config.icon;
  
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
          isCompleted ? 'bg-green-500' :
          isCurrent ? config.color : 'bg-slate-200'
        }`}>
          {isCompleted ? (
            <CheckCircle size={14} className="text-white" />
          ) : isCurrent ? (
            <Icon size={14} className="text-white" />
          ) : (
            <div className="w-2 h-2 rounded-full bg-slate-400" />
          )}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-8 ${isCompleted ? 'bg-green-400' : 'bg-slate-200'}`} />
        )}
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{item.time}</span>
          <button className="text-xs text-blue-500 font-medium flex items-center gap-1 hover:text-blue-600">
            <Eye size={10} />
            查看
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
            isCompleted ? 'bg-green-100 text-green-600' :
            isCurrent ? config.lightBg + ' ' + config.textColor :
            'bg-slate-100 text-slate-500'
          }`}>
            {isCompleted && <CheckCircle size={10} />}
            {isCurrent && <Icon size={10} />}
            {item.status}
          </span>
          <span className="text-sm text-slate-700">{item.action}</span>
        </div>
        {item.document && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <FileText size={10} />
            {item.document}
          </div>
        )}
      </div>
    </div>
  );
};

const DeliveryCard = ({ delivery, index, total, expanded, onToggle, onConfirm, onReject }) => {
  const config = DELIVERY_STATUS_CONFIG[delivery.status];
  const Icon = config.icon;
  const currentTimelineIndex = STATUS_ORDER.indexOf(delivery.status);
  const isInProgress = IN_PROGRESS_STATUSES.includes(delivery.status);
  const needConfirm = delivery.status === NEED_CONFIRM_STATUS;
  
  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden mb-3 transition-all ${
      needConfirm ? 'border-orange-300 ring-2 ring-orange-200' :
      isInProgress ? 'border-blue-200 ring-2 ring-blue-100' : 'border-slate-100'
    }`}>
      <div 
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${config.color} flex items-center justify-center shadow-sm ${needConfirm ? 'animate-pulse' : ''}`}>
              <Icon size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800">{delivery.deliveryNo}</span>
                {needConfirm && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded font-medium animate-pulse">
                    待确认
                  </span>
                )}
                {isInProgress && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">
                    进行中
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">批次 {index + 1}/{total}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${config.color} text-white shadow-sm`}>
              {delivery.status}
            </span>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}>
              <ChevronDown size={18} className="text-slate-400" />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
          {delivery.weight && (
            <span className="flex items-center gap-1">
              <Weight size={12} />
              <span className="font-medium">{delivery.weight}吨</span>
            </span>
          )}
          {delivery.amount && (
            <span className="flex items-center gap-1 text-red-600 font-bold">
              ¥{delivery.amount.toLocaleString()}
            </span>
          )}
          {delivery.vehicleNo && (
            <span className="flex items-center gap-1">
              <Truck size={12} />
              {delivery.vehicleNo}
            </span>
          )}
          {delivery.driverName && (
            <span className="flex items-center gap-1">
              <User size={12} />
              {delivery.driverName}
            </span>
          )}
        </div>
        
        {/* 待确认状态：收起时也显示结算确认区域 */}
        {needConfirm && !expanded && (
          <div className="mt-3 pt-3 border-t border-orange-200 bg-gradient-to-r from-orange-50 to-white -mx-4 -mb-4 px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold text-orange-600 flex items-center gap-1">
                <AlertCircle size={12} />
                结算确认
              </div>
              <div className="text-xs text-slate-500">
                {delivery.weight}吨 × ¥{delivery.unitPrice}/吨 = <span className="text-red-600 font-bold">¥{delivery.amount.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(delivery.id, '');
                }}
                className="flex-1 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
              >
                <XCircle size={14} />
                驳回
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm(delivery.id);
                }}
                className="flex-1 py-2 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-1 shadow-sm"
              >
                <CheckCircle size={14} />
                确认结算
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                className="py-2 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors flex items-center gap-1"
              >
                <Eye size={14} />
                查看详情
              </button>
            </div>
          </div>
        )}
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 bg-gradient-to-b from-slate-50/80 to-white">
          {/* 结算确认区域 - 放在最上方 */}
          {needConfirm && (
            <SettlementConfirmSection 
              delivery={delivery}
              onConfirm={onConfirm}
              onReject={onReject}
            />
          )}
          
          <div className={needConfirm ? 'pt-3' : 'pt-4'}>
            <div className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1">
              <Clock size={12} />
              状态流转
            </div>
            {delivery.timeline.map((item, i) => {
              const itemStatusIndex = STATUS_ORDER.indexOf(item.status);
              const isCompleted = itemStatusIndex < currentTimelineIndex;
              const isCurrent = itemStatusIndex === currentTimelineIndex;
              
              return (
                <TimelineNode 
                  key={i} 
                  item={item} 
                  isLast={i === delivery.timeline.length - 1}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                />
              );
            })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-slate-200">
            <div className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
              <FileText size={12} />
              相关单据
            </div>
            <div className="space-y-1">
              {delivery.timeline.filter(t => t.document).map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <FileText size={12} className="text-slate-400" />
                    <span>{item.document}</span>
                  </div>
                  <button className="text-xs text-blue-500 font-medium flex items-center gap-1 hover:text-blue-600">
                    <Download size={10} />
                    下载
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t border-slate-200">
            <div className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
              <Navigation size={12} />
              快捷操作
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button className="flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-lg text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-blue-200 transition-all">
                <Phone size={14} className="text-blue-500" />
                联系司机
              </button>
              <button className="flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-lg text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-blue-200 transition-all">
                <Navigation size={14} className="text-green-500" />
                导航基地
              </button>
              <button className="flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-lg text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-blue-200 transition-all">
                <FileText size={14} className="text-purple-500" />
                全部单据
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function OrderDetailPrototype3({ onBack, highlightDeliveryId, onConfirmSettlement, onRejectSettlement }) {
  const order = MOCK_ORDER;
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const deliveryRefs = useRef({});
  
  const needConfirmDeliveries = order.deliveries.filter(d => d.status === NEED_CONFIRM_STATUS);
  const hasNeedConfirm = needConfirmDeliveries.length > 0;
  
  useEffect(() => {
    if (highlightDeliveryId) {
      setExpandedIds(new Set([highlightDeliveryId]));
      setTimeout(() => {
        const element = deliveryRefs.current[highlightDeliveryId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }
    
    const firstNeedConfirmIndex = order.deliveries.findIndex(d => d.status === NEED_CONFIRM_STATUS);
    if (firstNeedConfirmIndex !== -1) {
      setExpandedIds(new Set([order.deliveries[firstNeedConfirmIndex].id]));
      return;
    }
    
    const firstInProgressIndex = order.deliveries.findIndex(d => 
      IN_PROGRESS_STATUSES.includes(d.status)
    );
    if (firstInProgressIndex !== -1) {
      setExpandedIds(new Set([order.deliveries[firstInProgressIndex].id]));
      return;
    }
    
    if (order.deliveries.length > 0) {
      setExpandedIds(new Set([order.deliveries[0].id]));
    }
  }, [highlightDeliveryId, order.deliveries]);
  
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(order.deliveries.map(d => d.id)));
    }
    setAllExpanded(!allExpanded);
  };
  
  const handleConfirm = (deliveryId) => {
    if (onConfirmSettlement) {
      onConfirmSettlement(deliveryId);
    }
  };
  
  const handleReject = (deliveryId, reason) => {
    if (onRejectSettlement) {
      onRejectSettlement(deliveryId, reason);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onBack} className="flex items-center gap-1 text-slate-600">
            <ChevronLeft size={20} />
            <span>返回</span>
          </button>
          <h1 className="font-bold text-slate-800">订单详情</h1>
          <div className="w-16" />
        </div>
      </div>
      
      {/* 待处理提示卡片 */}
      {hasNeedConfirm && (
        <div className="mx-4 mt-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <AlertCircle size={20} className="text-white" />
              </div>
              <div>
                <div className="text-white font-bold">有{needConfirmDeliveries.length}个结算单待确认</div>
                <div className="text-white/80 text-xs">请确认后进入付款流程</div>
              </div>
            </div>
            <button 
              onClick={() => {
                const firstNeedConfirm = needConfirmDeliveries[0];
                if (firstNeedConfirm) {
                  setExpandedIds(new Set([firstNeedConfirm.id]));
                  setTimeout(() => {
                    const element = deliveryRefs.current[firstNeedConfirm.id];
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }
              }}
              className="px-4 py-2 bg-white rounded-lg text-orange-600 text-sm font-bold shadow-sm hover:bg-orange-50 transition-colors"
            >
              立即处理
            </button>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-bold text-slate-800 text-lg">{order.orderNo}</div>
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                <MapPin size={12} />
                <span>{order.baseName}</span>
              </div>
            </div>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-blue-500 text-white shadow-sm">
              {order.status}
            </span>
          </div>
          
          <div className="grid grid-cols-4 gap-2 py-3 border-y border-slate-100 my-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500">{order.progress.waiting}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">待处理</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">{order.progress.inProgress}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">进行中</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500">{order.progress.needConfirm}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">待确认</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{order.progress.completed}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">已完成</div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <Package size={12} className="text-slate-400" />
              <span>{order.material}</span>
            </div>
            <div className="flex items-center gap-3">
              <span>预估: {order.estimatedWeight}吨</span>
              <span className="text-red-600 font-bold">¥{order.estimatedAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-bold text-slate-800">送货单列表</h2>
            <p className="text-xs text-slate-500">点击展开查看时间线和单据</p>
          </div>
          <button 
            onClick={toggleAll}
            className="flex items-center gap-1 text-xs text-blue-500 font-medium"
          >
            {allExpanded ? (
              <>
                <ChevronUp size={14} />
                全部折叠
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                全部展开
              </>
            )}
          </button>
        </div>
        
        {order.deliveries.map((delivery, index) => (
          <div key={delivery.id} ref={el => deliveryRefs.current[delivery.id] = el}>
            <DeliveryCard 
              delivery={delivery} 
              index={index} 
              total={order.deliveries.length}
              expanded={expandedIds.has(delivery.id)}
              onToggle={() => toggleExpand(delivery.id)}
              onConfirm={handleConfirm}
              onReject={handleReject}
            />
          </div>
        ))}
      </div>
      
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <button className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-blue-600 transition-colors">
          新增送货单
        </button>
      </div>
    </div>
  );
}